import { Permission, permissionsQuery, usePermissions } from "@/api/user";
import { queryClient } from "@/api/utils";
import { AccountMenu } from "@/components/layout/AccountMenu";
import { AppLayout } from "@/components/layout/AppLayout";
import {
  NavigationMenu,
  NavigationMenuHeader,
  NavigationMenuHeading,
  NavigationMenuLink,
} from "@/components/layout/NavigationMenu";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { ALL_COUNTRIES } from "@/constants/countries";
import { MoneyFnsProvider } from "@/modules/money-fns";
import { checkAdminRole, checkAuth } from "@/utils/router";
import { Center, Spinner } from "@givenwell/components";
import {
  AlignLeftIcon,
  BankIcon,
  BracketIcon,
  DashboardIcon,
  DollarIcon,
  EarthIcon,
  GraphIcon,
  HomeIcon,
  MailIcon,
  ManualBookIcon,
  PageSettingIcon,
  ReceiptIcon,
  SkullIcon,
  StoreIcon,
  TrashIcon,
  UserIcon,
  UserProtectionIcon,
  UsersIcon,
  WebcamVideoIcon,
} from "@givenwell/core-solid-icons";
import { createFileRoute, Outlet, useMatches } from "@tanstack/react-router";

export const Route = createFileRoute("/admin")({
  beforeLoad: async () => {
    await checkAuth();
    await checkAdminRole();
    // Admin pages hide sections the user can't access rather than letting them 403, so have the
    // permissions ready before the first render. prefetchQuery never throws; a failure just means
    // the gated sections stay hidden.
    await queryClient.prefetchQuery(permissionsQuery);
    return {
      sidebar: <Sidebar />,
    };
  },
  component: Layout,
});

function Sidebar() {
  const hasPermission = usePermissions();

  return (
    <NavigationMenu header={<NavigationMenuHeader />} footer={<AccountMenu role="admin" />}>
      <NavigationMenuLink to="/admin" icon={<HomeIcon />} activeOptions={{ exact: true }}>
        Overview
      </NavigationMenuLink>
      <NavigationMenuHeading>Moderation</NavigationMenuHeading>
      <NavigationMenuLink to="/admin/listing-review" icon={<PageSettingIcon />}>
        Listing Review
      </NavigationMenuLink>
      {hasPermission(Permission.Admin.ManageClaims) && (
        <NavigationMenuLink to="/admin/claims" icon={<ReceiptIcon />}>
          Claim Review
        </NavigationMenuLink>
      )}
      <NavigationMenuLink
        to="/admin/expiry"
        icon={<SkullIcon />}
        search={{ months: 3, group_by: "cohort", country: ALL_COUNTRIES }}
      >
        Expiring Points
      </NavigationMenuLink>
      <NavigationMenuLink to="/admin/account-owners" icon={<UserIcon />}>
        Account Owners
      </NavigationMenuLink>
      <NavigationMenuLink to="/admin/supporters" icon={<BankIcon />}>
        Supporters
      </NavigationMenuLink>
      <NavigationMenuLink to="/admin/suppliers" icon={<StoreIcon />}>
        Suppliers
      </NavigationMenuLink>
      <NavigationMenuLink to="/admin/members" icon={<UsersIcon />}>
        Members
      </NavigationMenuLink>
      <NavigationMenuLink to="/admin/surpluses" icon={<DollarIcon />}>
        Overpayments
      </NavigationMenuLink>
      <NavigationMenuHeading>Design</NavigationMenuHeading>
      <NavigationMenuLink to="/admin/themes" icon={<DashboardIcon />}>
        Themes
      </NavigationMenuLink>
      <NavigationMenuLink to="/admin/resources" icon={<WebcamVideoIcon />}>
        Resources
      </NavigationMenuLink>
      <NavigationMenuLink to="/admin/categories" icon={<AlignLeftIcon />}>
        Categories
      </NavigationMenuLink>
      <NavigationMenuLink to="/admin/resource-categories" icon={<AlignLeftIcon />}>
        Resource Cat.
      </NavigationMenuLink>
      <NavigationMenuLink to="/admin/countries" icon={<EarthIcon />}>
        Countries
      </NavigationMenuLink>
      <NavigationMenuLink to="/admin/legal" icon={<ManualBookIcon />}>
        Legal Terms
      </NavigationMenuLink>
      <NavigationMenuHeading>Comms</NavigationMenuHeading>
      <NavigationMenuLink to="/campaigns" icon={<MailIcon />}>
        Campaigns
      </NavigationMenuLink>
      <NavigationMenuHeading>Analysis</NavigationMenuHeading>
      <NavigationMenuLink to="/dashboard" icon={<GraphIcon />}>
        Reports
      </NavigationMenuLink>
      <NavigationMenuHeading>Nerd Stuff</NavigationMenuHeading>
      <NavigationMenuLink
        to="/admin/reports/$countryCode/daily-payouts"
        params={{ countryCode: "NZ" }}
        icon={<TrashIcon />}
      >
        Data Dump
      </NavigationMenuLink>
      {hasPermission(Permission.Admin.ManageRoles) && (
        <NavigationMenuLink to="/admin/staff" icon={<UserProtectionIcon />}>
          Staff
        </NavigationMenuLink>
      )}
      <NavigationMenuLink to="/admin/devtools" icon={<BracketIcon />}>
        DevTools
      </NavigationMenuLink>
    </NavigationMenu>
  );
}

function Layout() {
  const sidebar = useMatches({
    select(matches) {
      return matches?.findLast(match => match.context.sidebar)?.context.sidebar;
    },
  });
  return (
    <AppLayout sidebar={sidebar} role="admin">
      <MoneyFnsProvider
        fallback={
          <PageWrapper>
            <Center css={{ flex: "1 0 0px" }}>
              <Spinner />
            </Center>
          </PageWrapper>
        }
      >
        <Outlet />
      </MoneyFnsProvider>
    </AppLayout>
  );
}
